import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { SiteHeader, SiteFooter } from "../components/SiteChrome";
import { ClipboardTray } from "../components/ClipboardTray";
import { totalSymbols, formatCountPlus } from "../data/stats";
import { onCopy } from "../lib/copy";
import { recordCopy } from "../lib/library";
import { THEME_INIT_SCRIPT } from "../lib/theme";

// Registers the hand-rolled service worker (public/sw.js) after load. Kept as a
// string so it runs without a Vite PWA plugin — avoids duplicating a plugin
// already registered in vite.config.ts.
// When an UPDATED worker takes control (controllerchange, only if one already
// controlled the page — not on first install), reload once so the device drops
// any stale cached HTML/CSS/fonts and renders the fresh build automatically.
const SW_REGISTER_SCRIPT = `if("serviceWorker"in navigator){window.addEventListener("load",function(){navigator.serviceWorker.register("/sw.js").catch(function(){});var had=!!navigator.serviceWorker.controller,done=false;navigator.serviceWorker.addEventListener("controllerchange",function(){if(done||!had)return;done=true;window.location.reload()})})}`;

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="max-w-md text-center">
          <h1 className="text-gradient font-display text-7xl font-bold">404</h1>
          <h2 className="mt-4 font-display text-xl font-semibold">Symbol not found</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            That page doesn't exist — but {formatCountPlus(totalSymbols)} symbols do.
          </p>
          <div className="mt-6">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:opacity-90"
            >
              Back to home
            </Link>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-xl font-semibold">This page didn't load</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong. Try again or head home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Try again
          </button>
          <a
            href="/"
            className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-secondary"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      // Per-route head() owns the <title> + description; root provides only sitewide defaults.
      { name: "author", content: "CoolSymbols.cool" },
      { name: "theme-color", content: "#1a1530" },
      { property: "og:site_name", content: "CoolSymbols.cool" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "manifest", href: "/manifest.webmanifest" },
      // favicon.ico first: plenty of consumers (search results, older browsers,
      // bookmark bars, feed readers) fetch /favicon.ico by root convention and
      // ignore the SVG. It used to 404 into the SPA's HTML, which is why the
      // mark was not the same everywhere. Browsers that understand the SVG still
      // prefer it — it wins on being scalable.
      { rel: "icon", href: "/favicon.ico", sizes: "32x32" },
      { rel: "icon", href: "/icon.svg", type: "image/svg+xml" },
      { rel: "apple-touch-icon", href: "/icon-192.png" },
      // Self-hosted fonts, same-origin. Preload so they're ready at first paint
      // (no third-party round-trip, no font-swap CLS). crossOrigin is required on
      // font preloads even for same-origin to match the CORS fetch the CSS makes.
      { rel: "preload", href: "/fonts/inter.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
      { rel: "preload", href: "/fonts/space-grotesk.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "CoolSymbols.cool",
          url: "/",
          description: `Copy ${formatCountPlus(totalSymbols)} cool text symbols, emojis and special characters. Click to copy, paste anywhere.`,
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        {/* No-flash theme: must run before the body paints. */}
        <script dangerouslySetInnerHTML={{ __html: THEME_INIT_SCRIPT }} />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
        <script dangerouslySetInnerHTML={{ __html: SW_REGISTER_SCRIPT }} />
      </body>
    </html>
  );
}

function RootComponent() {
  // Phase 12 — feed every successful copy into the personal library's
  // "recently copied" list. Subscribing here keeps the wiring out of every
  // individual copy site and survives route transitions.
  useEffect(() => {
    return onCopy((meta) => {
      // Only record genuine single-symbol copies. Non-symbol copies (share
      // links, generator output, copy-all, format strings) carry no meta.symbol
      // and must never land in "recently copied".
      if (meta.symbol) recordCopy(meta.symbol);
    });
  }, []);
  return (
    <>
      <Outlet />
      <ClipboardTray />
    </>
  );
}
